import { useEffect, useState, FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import GppBadRoundedIcon from "@mui/icons-material/GppBadRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";

const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";

// Same palette as the PDF certificate
const CREAM = "#FBF8F1";
const GOLD = "#C9A55C";
const NAVY = "#0F1F45";
const TEXT_GRAY = "#4B5563";
const SERIF = `Georgia, "Times New Roman", serif`;

type VerifyResult = {
  valid: boolean;
  status?: string;
  message?: string;
  certificate_id?: string;
  student_name?: string;
  course_title?: string;
  instructor_name?: string;
  completion_date?: string;
  issued_at?: string;
  issuer?: string;
};

const formatDate = (value?: string) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? value
    : d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const DetailRow = ({ label, value }: { label: string; value?: string }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    justifyContent="space-between"
    spacing={{ xs: 0.3, sm: 2 }}
    sx={{ py: 1.4, borderBottom: `1px solid ${GOLD}33` }}
  >
    <Typography sx={{ fontSize: 13.5, color: TEXT_GRAY, flexShrink: 0 }}>{label}</Typography>
    <Typography
      sx={{
        fontSize: 14.5,
        fontWeight: 600,
        color: NAVY,
        textAlign: { xs: "left", sm: "right" },
        wordBreak: "break-word",
      }}
    >
      {value || "—"}
    </Typography>
  </Stack>
);

export default function VerifyCertificatePage() {
  const { code } = useParams<{ code?: string }>();
  const navigate = useNavigate();

  const [input, setInput] = useState(code || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [error, setError] = useState<string>("");

  const verify = async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;

    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await axios.get<VerifyResult>(
        `${apiUrl}/api/digital-twin/certificate/verify-certificate/${encodeURIComponent(trimmed)}`
      );
      setResult(data);
    } catch (err: any) {
      setError(
        err?.response?.status === 400
          ? "That doesn't look like a valid certificate ID or link."
          : "We couldn't check this certificate right now. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Verify automatically when opened from the QR code / link
  useEffect(() => {
    if (code) {
      setInput(code);
      verify(code);
    } else {
      setResult(null);
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;
    // Put the code in the URL so the result can be shared / refreshed
    navigate(`/verify-certificate/${encodeURIComponent(trimmed)}`);
  };

  const isValid = !!result?.valid;
  const isRevoked = result?.status === "revoked";
  const notFound = result && !result.valid && !isRevoked;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: CREAM,
        color: NAVY,
        display: "flex",
        justifyContent: "center",
        px: 2,
        py: { xs: 4, md: 8 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 640 }}>
        {/* Heading */}
        <Typography
          sx={{
            fontFamily: SERIF,
            fontWeight: 700,
            fontSize: { xs: 28, md: 36 },
            textAlign: "center",
            color: NAVY,
          }}
        >
          Certificate Verification
        </Typography>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mt: 1.5, mb: 1 }}>
          <Box sx={{ width: 70, height: "1px", background: GOLD }} />
          <Box sx={{ width: 8, height: 8, border: `1px solid ${GOLD}`, transform: "rotate(45deg)" }} />
          <Box sx={{ width: 70, height: "1px", background: GOLD }} />
        </Stack>
        <Typography sx={{ textAlign: "center", color: TEXT_GRAY, fontSize: 14.5, mb: 4 }}>
          Enter a Certificate ID (for example OCE-2026-847291) or scan the QR code on the certificate.
        </Typography>

        {/* Search */}
        <Box component="form" onSubmit={handleSubmit} sx={{ mb: 4 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              fullWidth
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Certificate ID"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: TEXT_GRAY }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  background: "#fff",
                  borderRadius: "10px",
                  color: NAVY,
                  "& fieldset": { borderColor: `${GOLD}88` },
                  "&:hover fieldset": { borderColor: GOLD },
                  "&.Mui-focused fieldset": { borderColor: NAVY },
                },
                "& input::placeholder": { color: TEXT_GRAY, opacity: 0.8 },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !input.trim()}
              sx={{
                px: 4,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                background: NAVY,
                color: "#fff",
                whiteSpace: "nowrap",
                "&:hover": { background: "#1c2f63" },
                "&.Mui-disabled": { background: `${NAVY}66`, color: "#fff" },
              }}
            >
              Verify
            </Button>
          </Stack>
        </Box>

        {/* Loading */}
        {loading && (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }}>
            <CircularProgress size={32} sx={{ color: NAVY }} />
            <Typography sx={{ color: TEXT_GRAY, fontSize: 14 }}>Checking certificate…</Typography>
          </Stack>
        )}

        {/* Request error */}
        {!loading && error && (
          <Box sx={{ p: 3, borderRadius: "14px", background: "#fff", border: "1px solid #FCA5A5", textAlign: "center" }}>
            <Typography sx={{ color: "#B91C1C", fontSize: 14.5 }}>{error}</Typography>
          </Box>
        )}

        {/* Valid */}
        {!loading && isValid && result && (
          <Box
            sx={{
              p: { xs: 2.5, md: 4 },
              borderRadius: "16px",
              background: "#fff",
              border: `1.5px solid ${GOLD}`,
              boxShadow: "0 10px 30px rgba(15,31,69,0.08)",
            }}
          >
            <Stack alignItems="center" spacing={1} sx={{ mb: 3 }}>
              <VerifiedRoundedIcon sx={{ fontSize: 54, color: "#15803D" }} />
              <Typography sx={{ fontFamily: SERIF, fontSize: 22, fontWeight: 700, color: "#15803D" }}>
                Valid Certificate
              </Typography>
              <Typography sx={{ color: TEXT_GRAY, fontSize: 14, textAlign: "center" }}>
                This certificate was issued and verified by {result.issuer || "OceSha"}.
              </Typography>
            </Stack>

            <Typography
              sx={{ fontFamily: SERIF, fontSize: { xs: 24, md: 30 }, fontWeight: 700, textAlign: "center", color: NAVY }}
            >
              {result.student_name}
            </Typography>
            <Typography sx={{ textAlign: "center", color: TEXT_GRAY, fontSize: 14, mt: 0.5 }}>
              has successfully completed
            </Typography>
            <Typography
              sx={{ fontFamily: SERIF, fontSize: { xs: 18, md: 21 }, fontWeight: 700, textAlign: "center", color: NAVY, mt: 0.5, mb: 3 }}
            >
              {result.course_title}
            </Typography>

            <DetailRow label="Certificate ID" value={result.certificate_id} />
            <DetailRow label="Instructor" value={result.instructor_name} />
            <DetailRow label="Completed on" value={formatDate(result.completion_date)} />
            <DetailRow label="Issued on" value={formatDate(result.issued_at)} />
          </Box>
        )}

        {/* Revoked */}
        {!loading && isRevoked && (
          <Box sx={{ p: 4, borderRadius: "16px", background: "#fff", border: "1.5px solid #FCA5A5", textAlign: "center" }}>
            <GppBadRoundedIcon sx={{ fontSize: 52, color: "#B91C1C" }} />
            <Typography sx={{ fontFamily: SERIF, fontSize: 22, fontWeight: 700, color: "#B91C1C", mt: 1 }}>
              Certificate Revoked
            </Typography>
            <Typography sx={{ color: TEXT_GRAY, fontSize: 14, mt: 1 }}>
              Certificate {result?.certificate_id} is no longer valid.
            </Typography>
          </Box>
        )}

        {/* Not found */}
        {!loading && notFound && (
          <Box sx={{ p: 4, borderRadius: "16px", background: "#fff", border: "1.5px solid #FCA5A5", textAlign: "center" }}>
            <GppBadRoundedIcon sx={{ fontSize: 52, color: "#B91C1C" }} />
            <Typography sx={{ fontFamily: SERIF, fontSize: 22, fontWeight: 700, color: "#B91C1C", mt: 1 }}>
              Certificate Not Found
            </Typography>
            <Typography sx={{ color: TEXT_GRAY, fontSize: 14, mt: 1 }}>
              {result?.message || "No certificate matches this ID."} Please check the ID and try again.
            </Typography>
          </Box>
        )}

        <Typography sx={{ textAlign: "center", color: TEXT_GRAY, fontSize: 12.5, mt: 5 }}>
          Issued and verified by <strong style={{ color: NAVY }}>OceSha</strong>
        </Typography>
      </Box>
    </Box>
  );
}
